import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Divider, Grid, Stack, Typography } from "@mui/material"
import { ResourcesListInfo } from "../../../createOrder/components/resource/ResourcesListInfo"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { ResourceItem, SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { DynamicFormField } from "../../../createOrder/components/form/DynamicFormField"
import { isHiddenConfigField } from "../../../createOrder/components/form/helpers"
import { isOngoing } from "../../../createOrder/components/resource/calendar/helpers"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import {
    BookingConfig,
    BookingCustomFieldsGroup,
    DynamicConfigKey,
} from "../../../createOrder/components/form/formTypes"

const useStyles = makeStyles()((theme) => ({
    infoText: {
        color: theme.palette.tailwind.slate[500],
    },
    wrap: {
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[950],
        background: theme.palette.tailwind.slate[50],
        padding: "1rem 1.5rem 1.5rem",
    },
    input: {
        "& .MuiInputBase-root": {
            borderRadius: ".75rem",
            background: theme.palette.common.white,
        },
    },
}))

type ResourcesInfoProps = Readonly<{
    config: BookingConfig
    user: SelectedMainUserState
    resourcesList: ResourceItem[]
    // hasMessagingEnabled: boolean
    handleClearResourceIds: () => void
}>

export function ResourcesInfo({
    config,
    user,
    resourcesList,
    // hasMessagingEnabled,
    handleClearResourceIds,
}: ResourcesInfoProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const [{ value: resourceIds }, , resourceIdsHelpers] =
        useField<BookingCustomFieldsGroup["resourceIds"]>("resourceIds")
    const [{ value: startDateTime }] = useField<BookingCustomFieldsGroup["startDateTime"]>("startDateTime")
    const [{ value: endDateTime }] = useField<BookingCustomFieldsGroup["endDateTime"]>("endDateTime")

    const handleDeleteResource = (id: number) => {
        if (resourceIds.length === 0) return

        if (resourceIds.length === 1) {
            handleClearResourceIds()
        } else {
            const filteredItems = resourceIds.filter((item) => item !== id.toString())
            resourceIdsHelpers.setValue(filteredItems)
        }
    }

    const bookingTime = dateTimeFactory.formatPeriod(
        dateTimeFactory.fromApi(startDateTime),
        dateTimeFactory.fromApi(endDateTime),
    )

    const serviceCenterMessageField = config.sections
        .flatMap((section) => section.groups.flatMap((group) => group.fields))
        .find(
            (field) =>
                field.key === DynamicConfigKey.SERVICE_CENTER_MESSAGE &&
                field.active !== false &&
                !isHiddenConfigField(field),
        )

    return (
        <Stack gap="1rem">
            <Grid container flexDirection="column" gap="1rem" className={classes.wrap}>
                <Stack gap=".5rem">
                    <LocalizedStrict
                        id="create-order-confirm-booking-selected-resources"
                        vars={{ count: resourcesList.length || 0 }}
                    >
                        <Typography variant="subtitle1">Selected resources ():</Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="create-order-confirm-booking-booking-time" vars={{ time: bookingTime }}>
                        <Typography variant="body1" className={classes.infoText}>
                            Booking time: {bookingTime}
                        </Typography>
                    </LocalizedStrict>
                </Stack>
                <Divider />
                <ResourcesListInfo
                    resourcesList={resourcesList}
                    {...(!isOngoing({ start: startDateTime, end: endDateTime }, dateTimeFactory) && {
                        handleDeleteResource,
                    })}
                />
            </Grid>
            {/* {serviceCenterMessageField && hasMessagingEnabled && ( */}
            {serviceCenterMessageField && (
                <Grid container flexDirection="column" gap="1rem" className={classes.wrap}>
                    <Stack gap=".75rem">
                        <LocalizedStrict id="create-order-confirm-booking-message-title">
                            <Typography variant="subtitle1">Messages to service center</Typography>
                        </LocalizedStrict>
                        <DynamicFormField
                            config={serviceCenterMessageField}
                            user={user}
                            updateTextfieldClassName={classes.input}
                        />
                    </Stack>
                </Grid>
            )}
        </Stack>
    )
}
